import { ClaimRequest, PaymentMethod } from '@bybeam/platform-types';
import Message from '@platform-ui-common/components/Message/index.js';
import ReviewPanel from '@platform-ui-common/components/ReviewPanel/index.js';
import { PaymentMethodDisplay } from '@platform-ui-common/utilities/payment.js';
import ClaimRoutes from '@platform-ui/claim/ClaimRoutes.js';
import ClaimForm from '@platform-ui/claim/components/ClaimForm/index.js';
import useClaim from '@platform-ui/claim/hooks/useClaim.js';
import useSubmitClaim from '@platform-ui/claim/hooks/utils/useClaimWithRedirect.js';
import { makeRoute } from '@platform-ui/utilities/Routes.js';
import { ReactNode } from 'react';
import { useHistory } from 'react-router';
import ReviewBox, { ReviewBoxType } from './ReviewBox.js';

const reviewContent: Record<
  Exclude<PaymentMethod, PaymentMethod.External | PaymentMethod.Zelle>,
  {
    header: string;
    subHeader: ReactNode;
    infoText?: string;
    reset?: boolean;
    boxes: { displayName: string; editRoute?: string; type?: 'address' | 'email'; key?: string }[];
  }
> = {
  [PaymentMethod.DirectDeposit]: {
    header: 'Please confirm your direct deposit information before submitting',
    subHeader:
      'Please make sure the direct deposit information you’ve entered matches that of your bank.',
    boxes: [
      {
        displayName: PaymentMethodDisplay[PaymentMethod.DirectDeposit],
        editRoute: ClaimRoutes.ACH_ROUTING_NUMBER,
      },
    ],
  },
  [PaymentMethod.Check]: {
    header: 'Please confirm your mailing address before submitting',
    subHeader: 'Please make sure the mailing address you entered is correct.',
    boxes: [
      {
        displayName: 'Mailing Address',
        editRoute: ClaimRoutes.MAILING_ADDRESS,
        type: ReviewBoxType.MailingAddress,
      },
    ],
  },
  [PaymentMethod.PhysicalCard]: {
    header: 'Please confirm your physical prepaid card information',
    subHeader:
      'Please make sure your information is correct. The card will be sent to your mailing address. You will use your email address to activate the card.',
    infoText: 'Don’t have access to this email? Consider selecting a different payment method.',
    reset: true,
    boxes: [
      {
        displayName: 'Mailing Address',
        editRoute: ClaimRoutes.MAILING_ADDRESS,
        type: ReviewBoxType.MailingAddress,
        key: 'address',
      },
      {
        displayName: 'Email Address',
        type: ReviewBoxType.EmailAddress,
      },
    ],
  },
  [PaymentMethod.VirtualCard]: {
    header: 'Please confirm your virtual prepaid card information',
    subHeader:
      'Please make sure your information is correct. You will receive an email with further instructions from Akimbo Card to the email address provided.',
    infoText: 'Don’t have access to this email? Consider selecting a different payment method.',
    reset: true,
    boxes: [
      {
        displayName: 'Mailing Address',
        editRoute: ClaimRoutes.MAILING_ADDRESS,
        type: ReviewBoxType.MailingAddress,
        key: 'address',
      },
      {
        displayName: 'Email Address',
        type: ReviewBoxType.EmailAddress,
      },
    ],
  },
};

function ClaimReview(): JSX.Element {
  const history = useHistory();
  const { fulfillment, method, payload, setPayload } = useClaim<ClaimRequest>();
  const { submitClaim, error, loading } = useSubmitClaim(fulfillment, method, payload);
  const { header, subHeader, boxes, infoText, reset } = reviewContent[method];

  const onEdit = (editRoute: ClaimRoutes, key?: string): void => {
    if (!loading) {
      setPayload(key ? { ...payload, [key]: null } : null);
      history.push(makeRoute(editRoute, { fulfillmentId: fulfillment.id }));
    }
  };

  const onReset = (): void => {
    setPayload(null);
    history.push(makeRoute(ClaimRoutes.PAYMENT_METHOD, { fulfillmentId: fulfillment.id }));
  };

  const helperMessage = (
    <div className="flex flex-col gap-y-4">
      {infoText && <Message variant="info">{infoText}</Message>}
      {error?.retryable && <Message variant="warning">{error?.message}</Message>}
    </div>
  );

  return (
    <ClaimForm
      header={header}
      subHeader={subHeader}
      onSubmit={submitClaim}
      onReset={reset && onReset}
      loading={loading}
      isSubmit
      helperText={helperMessage}
    >
      <div className="flex flex-col text-left max-w-[460px] pt-8 gap-y-10">
        {boxes.map(({ displayName, editRoute, type, key }) => (
          <ReviewPanel
            key={displayName}
            title={displayName}
            onEdit={editRoute && ((): void => onEdit(editRoute, key))}
            isComplete
          >
            {payload && Object.keys(payload).length > 0 && (
              <ReviewBox type={type} payload={payload} />
            )}
          </ReviewPanel>
        ))}
      </div>
    </ClaimForm>
  );
}

export default ClaimReview;
